import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link, useRouteMatch, useParams } from "react-router-dom"
import { Table, Skeleton, Popconfirm, notification, Dropdown, Menu, Button, Modal, Checkbox, Radio, Select  } from "antd"
import {
    UploadOutlined,
    FilterOutlined,
    FileTextOutlined,
    EditOutlined,
    DeleteOutlined,
    DownOutlined,
    FolderOutlined,
    RetweetOutlined,
    CheckOutlined,
    CloseOutlined,
    CloseSquareOutlined,
    ArrowRightOutlined,
    ArrowLeftOutlined,
    SearchOutlined,
} from "@ant-design/icons"
import moment from "moment"
import { allObjectValuesEmpty } from "../../../functions/objectFunctions/allObjectValuesEmpty"
import ColumnHeaderTitle from "../../../components/utilityComponents/table/ColumnHeaderTitle"
import useConfirmationModal from "../../alertsPanel/ShowConfirm"
import Role from "../../userRoles/Role"
import { formatCurrency } from "../../../lib/utilities"
import {
    achSummaryCsv,
    checkForPaidBatches,
    checkForVendorPayments,
    createNachaFile,
    deletePaymentBatch,
    fetchGlobalBatches,
    fetchGlobalBatchesFiltered,
    fetchGlobalBatchesForType,
    generateCsv,
    generateGlobalVoucherCsv,
    selectAllBatchesPagination,
    selectBankValue,
    selectCheckedBatches,
    selectGlobalBatches,
    selectGlobalBatchesCount,
    selectLoading,
    selectSumOfBatched,
    selectSumOfFinalized,
    selectSumOfType,
    selectTodaysBatchesPagination,
    setCheckedBatches,
    unbatchBatches,
    updatePaidProcess,
    updateTempPayType,
} from "../globalBatchSlice"
import { generateVoucherCsv } from "../../financials/paymentBatches/paymentBatchesSlice"
import useExportConfirmationModal from "../../alertsPanel/ShowConfirmGlobalExport"
import { checkIfTwoDecimalPlaces } from "../consts/helperFunctions"
import { tableFiltersApplied } from "../../../functions/objectFunctions/tableFiltersApplied"
import ColumnSearchFilter from "../../../components/utilityComponents/table/ColumnSearchFilter"


const { Option } = Select

// Splits ACH batches into groups per ACH color export type (vendor has an ACH color payment method) and plain ACH batches
const splitByAchExportType = (batches) => {
    const colorGroups = {}
    const plainBatches = []
    batches.forEach((batch) => {
        if (batch.ach_export_type) colorGroups[batch.ach_export_type] = (colorGroups[batch.ach_export_type] || []).concat(batch)
        else plainBatches.push(batch)
    })
    return { colorGroups, plainBatches }
}
export default function GlobalBatchView({ scopedByClient }) {
    /////*** react router start ***/////
    const { url } = useRouteMatch()
    // console.log(useParams())
    const isTodaysBatches = url.indexOf("todays_batches") > 0
    const params = useParams()
    const usesType = params.type ? params.type : false

    /////*** redux start ***/////
    const dispatch = useDispatch()
    const globalBatches = useSelector(selectGlobalBatches)
    const total = useSelector(selectGlobalBatchesCount)
    const pagination = useSelector(isTodaysBatches ? selectTodaysBatchesPagination : selectAllBatchesPagination)
    const loading = useSelector(selectLoading)
    const checkedBatches = useSelector(selectCheckedBatches)
    const bankValue = useSelector(selectBankValue)
    const sumOfFinalized = useSelector(selectSumOfFinalized)
    const sumOfBatched = useSelector(selectSumOfBatched)
    const sumOfType = useSelector(selectSumOfType)
    /////*** redux end ***/////

    /////*** useState start ***/////
    const [loaded, setLoaded] = useState(false)
    const [filters, setFilters] = useState({})
    const [sort, setSort] = useState({
        column: "payer_name",
        direction: "asc",
    })
    const [openModal, setOpenModal] = useState(false)
    const [openACHModal, setOpenACHModal] = useState(false)
    const [batchForExport, setBatchForExport] = useState(null)
    const [allBatchesSelected, setAllBatchesSelected] = useState(false)
    const [selectedACHExport, setSelectedACHExport] = useState('standard')
    const [exclusiveBatch, setExclusiveBatch] = useState(null)
    /////*** useState end ***/////

    /////*** useEffect start ***/////
    useEffect(() => {
        if (!loaded) {
            setLoaded(true)
            handleFetchInitialGlobalBatches()
        }
    }, [loaded, url, dispatch, pagination]) // eslint-disable-line

    useEffect(() => {
        dispatch(setCheckedBatches([]))
        handleFetchInitialGlobalBatches()
    }, [url])
    /////*** useEffect end ***/////

    const handleFetchInitialGlobalBatches = () => {
        if (!usesType) {
            dispatch(fetchGlobalBatches(pagination, isTodaysBatches))
        } else {
            dispatch(fetchGlobalBatchesForType(pagination, usesType))
        }
    }

    const handleTableChange = (newPagination, sorter) => {
        if (!usesType) {
            dispatch(fetchGlobalBatches(newPagination, isTodaysBatches))
        } else {
            dispatch(fetchGlobalBatchesForType(newPagination, usesType))
        }
    }

    const handleFilterBatch = (newPagination = null, newSort = null) => {
        let filterJson = {
            filters: filters,
            pagination: !!newPagination ? newPagination : pagination,
            sort: !!newSort ? newSort : sort,
        }
        return dispatch(fetchGlobalBatchesFiltered(filterJson, usesType))
    }

    const handleSortGlobalBatchesChange = (column) => () => {
        let newDirection = sort.direction === "asc" ? "desc" : "asc"
        let newSort = {
            column: column,
            direction: newDirection,
        }
        setSort(newSort)
        handleFilterBatch(null, newSort)
    }

    const renderResetFilters = () => {
        if (allObjectValuesEmpty(filters)) {
            return null
        }
        return (
            <div className={"actions-alternate-buttons"}>
                <a
                    href="javascript:;"
                    onClick={() => {
                        handleFetchInitialGlobalBatches()
                        setFilters({})
                        setSort({ column: "name", direction: "asc" })
                    }}>
                    <FilterOutlined />
                    Reset Filters
                </a>
            </div>
        )
    }

    const renderColumnHeaderTitle = (title, key = '') => {
        return <ColumnHeaderTitle title={title} handleSortChange={handleSortGlobalBatchesChange} sort={sort} replacementTitle={key} />
    }

    const voucherCsv = async (paymentBatchId) => {
        notification.open({
            message: "Vendor Batch Voucher CSV started",
            duration: 0.8,
        })

        dispatch(generateVoucherCsv(paymentBatchId))
    }

    const showConfirm = useConfirmationModal()
    const handleDeleteClick = (id) => {
        showConfirm(deletePaymentBatch, id)
    }

    const showExportConfirm = useExportConfirmationModal()
    const handleExportBatches = async () => {
        // const response = await dispatch(checkForVendorPayments())
        const checkPaidBatches = await dispatch(checkForPaidBatches())

        // if (response.data.count != 0) {
        //     notification.open({
        //         type: "error",
        //         message: "One or more bills do not have a vendor payment",
        //         duration: 2,
        //         description: "All bills in all batches must have a vendor payment.",
        //     })
        // } else
         if (checkPaidBatches.data.count == 0) {
            notification.open({
                type: "error",
                message: "No paid batches.",
                duration: 2,
                description: "There are no batches which are paid that have not already been finalized today.",
            })
        } else {
            showExportConfirm(globalVoucherCsv)
        }
    }

    const globalVoucherCsv = async () => {
        // if(!checkForPaidBatch(checkedBatches)){
        //     notification.error({
        //         message: "Unpaid batches",
        //         description: 'All selected batches must be paid before finalizing them with the export.',
        //         duration: 2,
        //     })
        //     return
        // }
        notification.open({
            message: "Quickbook export has started",
            duration: 0.8,
        })

        const batchIds = checkedBatches.map((batch, i) => {
            return batch.global_batch_id
        })

        await dispatch(generateGlobalVoucherCsv())
    }

    const handleModal = (record = null, open = false) => {
        setOpenModal(open)
        setBatchForExport(record)
    }

    const handleACHModal = (open = false) => {
        setOpenACHModal(open)
        setSelectedACHExport('standard')
    }

    const handleExportAs = async (type, array = false, achType = false) => {
        // const response = await dispatch(checkForVendorPayments(type))
        // if (response.data.count != 0) {
        //     notification.open({
        //         type: "error",
        //         message: "One or more bills do not have a vendor payment",
        //         duration: 2,
        //         description: "All bills in all batches must have a vendor payment.",
        //     })
        // } else {
            if(type == 'ACH'){
                exportACH(achType)
            }
            else{
                exportAs(type, array)
            }
            // showExportConfirm(
            //     exportAs(type, array),
            //     `Create export for ${type}?`,
            //     `Pressing OK will create an export file with the selected batches and remove them from this table. These batches will still appear in "Today's Batches" for the quickbook export.`
            // )
        // }
    }

    const exportAs = async (type, array = false) => {
        const batchIds = []
        if (array && globalBatches.length == 0) {
            notification.error({
                message: "Missing batches",
                description: "Must have one or more batches to export",
                duration: 1.5,
            })
            return
        }
        let bills
        if (!array) {
            bills = batchForExport
        } else {
            const arr = typeof array == "object" ? array : globalBatches
            bills = arr.map((batch, i) => {
                batchIds.push(batch.global_batch_id)
                return batch.bills
            })
            bills = bills.flat()
        }
        const billIds = bills.map((bill, i) => {
            return bill.id
        })

        if (type != "OP" && type != "NP") {
            notification.open({
                message: `${type} export has started`,
                duration: 1,
            })
        }

        let res = ""
        // if(!array){
        //     switch (type){
        //         case 'Default':
        //             res = await dispatch(generateCsv(billIds, batchForExport.bills_payment_method, 'global_batch_default_export'))
        //             // console.log(res)
        //             return
        //         case 'Quick Books':
        //             res = await dispatch(generateCsv(billIds, 'quick_books', 'global_batch_qbo_export'))
        //             // console.log(res)
        //             return
        //         case 'OP/Check':
        //             res = await dispatch(generateCsv(billIds, 'OP', 'global_batch_op_check_export'))
        //             // console.log(res)
        //             return
        //         default:
        //             return { error: "Something went wrong" }
        //     }
        // }
        // else{
        const { temp_pay_method, bills_payment_method } = globalBatches[0]
        const paymentMethod = temp_pay_method ? temp_pay_method : bills_payment_method
        if (usesType != "OP" && usesType != "NP") await dispatch(generateCsv(billIds, paymentMethod))
        await dispatch(updatePaidProcess(batchIds, usesType, true, usesType == "NP" ? true : false))
        await dispatch(setCheckedBatches([]))
        // }
    }

    function getBatchInfoFromArr(arr) {
        const batchIds = []
        let bills = arr.map((batch, i) => {
            batchIds.push(batch.global_batch_id)
            return batch.bills
        })

        bills = bills.flat()
        const billIds = bills.map((bill, i) => {
            return bill.id
        })

        return { batchIds: batchIds, billIds: billIds, bills: bills }
    }

    const exportOP = async () => {
        const billIds = getBatchInfoFromArr(checkedBatches).billIds

        notification.open({
            message: `OP export has started`,
            duration: 1,
        })

        await dispatch(generateCsv(billIds, "OP"))
        await dispatch(setCheckedBatches([]))
    }


    // Copied function from GlobalBatchTable.jsx and reworked it for batches
    function checkForExclusiveBatch(){
        let tempExclusive = null
        let isExclusive = false

        for (let i = 0; i < globalBatches.length; i++) {
            const batch = globalBatches[i]
            const { bills } = batch
            const { vendor_id, vendor_name, is_exclusive } = bills[0]

            // // There's a mix of exclusive and not exclusive bills
            if (i > 0 && is_exclusive && !isExclusive) {
                // If non-exclusive bill is found first and then an exclusive one
                notification.error({
                    duration: 2,
                    message: "Not allowed",
                    description: `Batches with the vendor ${vendor_name} must be managed separately.`,
                })
                return false
            } else if (i > 0 && !is_exclusive && isExclusive) {
                // If exclusive bill is found first and then a non-exclusive one
                notification.error({
                    duration: 2,
                    message: "Not allowed",
                    description: `Batches with the vendor ${tempExclusive.name} must be managed separately.`,
                })
                return false
            }

            if (is_exclusive && !isExclusive) {
                isExclusive = true
                tempExclusive = { name: vendor_name, id: vendor_id }
            }

            // Two exclusive vendors which cannot be batched together
            if (is_exclusive && isExclusive && vendor_id != tempExclusive.id) {
                notification.error({
                    duration: 2,
                    message: "Not allowed",
                    description: `Batches with the vendor ${tempExclusive.name} must be managed separately.`,
                })
                return false
            }
        }

        return true
    }

    const exportACH = async (type = "standard") => {
        const plainType = type || selectedACHExport
        if(plainType.includes('fake')){
            notification.open({
                message: `The selection was put as a simple test :)`,
                duration: 1.5,
            })
            return
        }

        const { batchIds } = getBatchInfoFromArr(checkedBatches)

        const { bills } = checkedBatches[0]
        const { is_exclusive, vendor_name } = bills[0]

        const exclusive = is_exclusive ? vendor_name.replaceAll("&", "and") : null
        // Batches with an ACH color payment method export in their color; plain ACH batches use the selected export type
        let { colorGroups, plainBatches } = exclusive ? { colorGroups: {}, plainBatches: checkedBatches } : splitByAchExportType(checkedBatches)
        // Two exports of the same type started in the same second get the same file name and overwrite each other, so export them together
        if (plainBatches.length > 0 && colorGroups[plainType]) {
            colorGroups[plainType] = colorGroups[plainType].concat(plainBatches)
            plainBatches = []
        }

        notification.open({
            message: `ACH export has started`,
            duration: 1,
        })

        await dispatch(updatePaidProcess(batchIds, usesType, true))
        for (const [achExportType, batches] of Object.entries(colorGroups)) {
            await dispatch(generateCsv(getBatchInfoFromArr(batches).billIds, "ACH", achExportType))
        }
        if (plainBatches.length > 0) {
            await dispatch(generateCsv(getBatchInfoFromArr(plainBatches).billIds, "ACH", plainType, null, exclusive))
        }
        await dispatch(setCheckedBatches([]))
    }

    // Made this function just in case there's a need to export all the batches separately instead of as a group
    const exportAsIndividual = async () => {
        const batchIds = []
        notification.open({
            message: `Export creation has started`,
            duration: 1,
        })
        for (let i = 0; i < checkedBatches.length; i++) {
            const { id } = checkedBatches[i]
            batchIds.push(id)
            await dispatch(generateVoucherCsv(id))
        }
        notification.success({
            message: `Export creation has finished`,
            duration: 1,
        })
    }

    const updateChecked = (e, batch, array = false) => {
        if (e.target.checked) {
            const { vendor_name, vendor_id, is_exclusive } = batch.bills[0]
            if (!exclusiveBatch) {
                if (is_exclusive) {
                    if (checkedBatches.length > 0) {
                        notification.error({
                            duration: 2,
                            message: "Not Allowed",
                            description: `Batches with the vendor: ${vendor_name} can only be managed with batches with that same vendor.`,
                        })
                        return
                    }
                    setExclusiveBatch({ name: vendor_name, id: vendor_id })
                }
            } else {
                if (exclusiveBatch.id != vendor_id) {
                    notification.error({
                        duration: 2,
                        message: "Not Allowed",
                        description: `Batches with the vendor: ${exclusiveBatch.name} can only be managed with batches with that same vendor.`,
                    })
                    return
                }
            }
            dispatch(setCheckedBatches(checkedBatches.concat(!array ? batch : array)))
        } else {
            if (!array) {
                const { id } = batch
                dispatch(setCheckedBatches(checkedBatches.filter((batch) => batch.id !== id)))
                if(checkedBatches.length == 1) setExclusiveBatch(null)
            } else {
                const idArr = []
                for (let i = 0; i < array.length; i++) {
                    const id = array[i].id
                    idArr.push(id)
                }
                const updatedCheckedBatches = checkedBatches.filter((bill) => !idArr.includes(bill.id))
                dispatch(setCheckedBatches(checkedBatches.filter((batch) => !idArr.includes(batch.id))))
                if(updatedCheckedBatches.length == 0) setExclusiveBatch(null) 
            }
        }
    }

    const checkBatchId = (checkId) => {
        for (let i = 0; i < checkedBatches.length; i++) {
            const id = checkedBatches[i].id
            if (id == checkId) return true
        }
        return false
    }

    function checkForAllBatchesSelected() {
        if (globalBatches.length == 0) return false
        for (let i = 0; i < globalBatches.length; i++) {
            if (globalBatches[i].is_paid) continue
            if (!checkBatchId(globalBatches[i].id)) return false
        }
        return true
    }

    // function checkForPaidBatch(checkArr = false) {
    //     const arr = checkArr ? checkArr : globalBatches
    //     if (checkArr.length == 0) return false
    //     for (let i = 0; i < checkArr.length; i++) {
    //         if (!checkArr[i].is_paid) return false
    //     }
    //     return true
    // }

    async function handleUnbatch() {
        const ids = checkedBatches.map((batch, i) => {
            return batch.id
        })
        dispatch(unbatchBatches(ids))
        await dispatch(setCheckedBatches([]))
    }

    async function handleUpdatePayMethod(type) {
        const ids = checkedBatches.map((batch, i) => {
            return batch.global_batch_id
        })
        dispatch(updateTempPayType(ids, type, usesType))
        dispatch(setCheckedBatches([]))
    }

    function checkIfSelectionGoesToNP() {
        for (let i = 0; i < checkedBatches.length; i++) {
            const batchTotal = parseFloat(checkedBatches[i].batch_total)
            const tempMethod = checkedBatches[i].temp_pay_method
            if (batchTotal > 1 || (tempMethod == "NP" && usesType != "NP")) return false
        }
        return true
    }

    const renderSearchOutlinedButton = (filterKey) => {
        return <SearchOutlined style={{ fontSize: "16px", color: tableFiltersApplied(filterKey, filters) ? "#1890ff" : undefined }} />
    }


    return (
        <>
            {!loading && usesType == "ACH" && <div>* = Can only be managed individually or with batches with the same vendor</div>}
            <div className="muc-table card">
                <Modal
                    onCancel={() => handleACHModal()}
                    open={openACHModal}
                    footer={[
                        <Button
                            type="primary"
                            onClick={() => {
                                handleExportAs(usesType, checkedBatches, selectedACHExport)
                                handleACHModal()
                                // exportAsIndividual()
                            }}>
                            Export
                        </Button>,
                        <Button type="back" onClick={() => handleACHModal()}>
                            Cancel
                        </Button>,
                    ]}>
                    <h4>Select export type:</h4>
                    {checkedBatches.some((batch) => batch.ach_export_type) && (
                        <p>
                            Applies to {checkedBatches.filter((batch) => !batch.ach_export_type).length} ACH batch(es).{" "}
                            {checkedBatches.filter((batch) => batch.ach_export_type).length} batch(es) with an ACH color payment method will export automatically in their color.
                        </p>
                    )}
                    <br />
                    <Select
                        // disabled={!isEmpty(bill)}
                        style={{ width: "100%" }}
                        showSearch
                        placeholder={"Select"}
                        filterOption={true}
                        defaultActiveFirstOption={false}
                        showArrow={true}
                        // onSearch={handleAccountSearch}
                        onChange={(e) => setSelectedACHExport(e)}>
                        {[
                            // {
                            //     value: "standard",
                            //     label: "Standard",
                            // },
                            // {
                            //     value: "nacha_plus",
                            //     label: "NACHA+",
                            // },
                            // {
                            //     value: "ach_2",
                            //     label: "ACH 2 Internal",
                            // },
                            {
                                value: "ach_bank",
                                label: "ACH Bank",
                            },
                            {
                                value: "ach_705_RMR",
                                label: "ACH 705 RMR",
                            },
                            {
                                value: "ach_RMR",
                                label: "ACH RMR",
                            },
                            {
                                value: "ach_color_green",
                                label: "ACH GREEN",
                            },
                            {
                                value: "ach_color_peach",
                                label: "ACH PEACH",
                            },
                            {
                                value: "ach_color_light_blue",
                                label: "ACH LIGHT BLUE",
                            },
                            {
                                value: "ach_color_pink",
                                label: "ACH PINK",
                            },
                            {
                                value: "ach_color_purple",
                                label: "ACH PURPLE",
                            },
                            {
                                value: "ach_color_red",
                                label: "ACH RED",
                            },

                            // {
                            //     value: "fake1",
                            //     label: "New Item 1",
                            // },
                            // {
                            //     value: "fake2",
                            //     label: "New Item 2",
                            // },
                            // {
                            //     value: "fake3",
                            //     label: "New Item 3",
                            // },
                            // {
                            //     value: "fake4",
                            //     label: "New Item 4",
                            // },
                            // {
                            //     value: "fake5",
                            //     label: "New Item 5",
                            // },
                            // {
                            //     value: "fake6",
                            //     label: "New Item 6",
                            // },
                        ].map((o) => (
                            <Option key={o.label} value={o.value}>
                                {o.label}
                            </Option>
                        ))}
                    </Select>
                    {/* <Radio.Group
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                            fontWeight: "normal",
                            fontSize: 10,
                        }}
                        onChange={(e) => setSelectedACHExport(e.target.value)}
                        value={selectedACHExport}
                        options={[
                            // TODO: Add this to const
                            {
                                value: "standard",
                                label: "Standard",
                            },
                            // {
                            //     value: "CDD",
                            //     label: "CDD",
                            // },
                            // {
                            //     value: "CDD+",
                            //     label: "CDD+",
                            // },
                            // {
                            //     value: "NACHA",
                            //     label: "NACHA",
                            // },
                            {
                                value: "nacha_plus",
                                label: "NACHA+",
                            },
                            {
                                value: "ach_2",
                                label: "ACH 2 Internal",
                            },
                            {
                                value: "ach_bank",
                                label: "ACH Bank",
                            },
                        ]}
                    /> */}
                </Modal>
                <Modal
                    onCancel={() => handleModal()}
                    open={openModal}
                    footer={[
                        <Button
                            type="primary"
                            onClick={() => {
                                handleModal()
                                // Only plain ACH batches need an export type; ACH color batches export in their color
                                if (usesType == "ACH" && !exclusiveBatch && checkedBatches.some((batch) => !batch.ach_export_type)) {
                                    setOpenACHModal(true)
                                } else {
                                    handleExportAs(usesType, checkedBatches)
                                }
                                // exportAsIndividual()
                            }}>
                            OK
                        </Button>,
                        <Button
                            type="back"
                            onClick={() => {
                                handleModal()
                                // handleExportAs(usesType)
                            }}>
                            Cancel
                        </Button>,
                    ]}>
                    {usesType != "OP" && usesType != "NP" && (
                        <>
                            <h4>Create export for {usesType}?</h4>
                            <p>
                                Pressing OK will create an export file with all the bills in each of the selected batches and then remove these batches from this table. These batches will still appear
                                in "Today's Batches" for the quickbook export.
                            </p>
                        </>
                    )}
                    {usesType == "OP" && (
                        <>
                            <h4>Make {usesType} payment?</h4>
                            <p>
                                Pressing OK will create a payment for each of the selected batches and then remove these batches from this table. These batches will still appear in "Today's Batches"
                                for the quickbook export.
                            </p>
                        </>
                    )}
                    {usesType == "NP" && (
                        <>
                            <h4>Finalize selected batches?</h4>
                            <p>
                                Pressing OK will finalize each of the selected batches and then remove these batches from this table. These batches will NOT appear in "Today's Batches" for the
                                quickbook export.
                            </p>
                        </>
                    )}
                    {/* {usesType == "ACH" && exclusiveBatch && (
                        <>
                            <h4>Create export for {usesType}?</h4>
                            <p>
                                Pressing OK will create an export file with all the bills in each of the selected batches and then remove these batches from this table. These batches will still appear
                                in "Today's Batches" for the quickbook export.
                            </p>
                        </>
                    )} */}
                </Modal>
                <div className="card-body">
                    <div>
                        <div className="card-body-heading">
                            <div>
                                <div className="badged-header">
                                    <h2>{isTodaysBatches ? `Today's ${usesType ? usesType : ""} Batches` : "Global Batches"}</h2>
                                    <div className="badge">{total}</div>
                                </div>
                                <div className="badged-header" style={{ gap: 10, color: "white" }}>
                                    <h4>Bank Value After Today's Batch Total: </h4>
                                    <div className="global-batch-filter-label-text">${checkIfTwoDecimalPlaces((bankValue.value - sumOfBatched).toLocaleString())}</div>
                                </div>
                                {usesType && (
                                    <div className="badged-header" style={{ gap: 10, color: "white", marginTop: "-2.2%" }}>
                                        <h4>{usesType} Batches Total: </h4>
                                        <div className="global-batch-filter-label-text">${checkIfTwoDecimalPlaces(sumOfType.toLocaleString())}</div>
                                    </div>
                                )}
                            </div>
                            <div className="actions">
                                <div className="table-action-links">
                                    {renderResetFilters()}

                                    {/* <Role action="create" model="payment_batch">
                                    <Link to={`${url}/new`}>
                                        <PlusOutlined />
                                        Add New
                                    </Link>
                                </Role> */}

                                    {/* <a href="javascript:;" onClick={() => console.log("Import")}>
                                    <UploadOutlined />
                                    Import
                                </a> */}

                                    {isTodaysBatches && checkedBatches.length > 0 && (
                                        <>
                                            <a href="#" onClick={() => handleUnbatch()}>
                                                <CloseSquareOutlined />
                                                Unbatch {checkedBatches.length == 1 ? "selection" : "selections"}
                                            </a>
                                            {checkIfSelectionGoesToNP() && (
                                                <a href="#" onClick={() => handleUpdatePayMethod(usesType == "NP" ? null : "NP")}>
                                                    {usesType == "NP" ? <ArrowLeftOutlined /> : <ArrowRightOutlined />}
                                                    Move {checkedBatches.length == 1 ? "selection" : "selections"} {usesType == "NP" ? "back to default" : "to NP"}
                                                </a>
                                            )}
                                            {!exclusiveBatch && (
                                                <a href="#" onClick={() => handleUpdatePayMethod(usesType == "OP" ? null : "OP")}>
                                                    {usesType == "OP" ? <ArrowLeftOutlined /> : <ArrowRightOutlined />}
                                                    Move {checkedBatches.length == 1 ? "selection" : "selections"} {usesType == "OP" ? "back to default" : "to OP"}
                                                </a>
                                            )}
                                            {usesType && (usesType == "Check" || usesType == "Avid") && (
                                                <a href="#" onClick={() => handleUpdatePayMethod(usesType == "Avid" ? null : "Avid")}>
                                                    {usesType == "Avid" ? <ArrowLeftOutlined /> : <ArrowRightOutlined />}
                                                    Move {checkedBatches.length == 1 ? "selection" : "selections"} {usesType == "Avid" ? "back to default" : "to Avid"}
                                                </a>
                                            )}
                                            {usesType && (
                                                <>
                                                    {usesType != "ACH" && usesType != "OP" && usesType != "NP" && (
                                                        <button disabled={loading} onClick={() => setOpenModal(true)}>
                                                            <UploadOutlined style={{ paddingRight: "10px" }} />
                                                            Export as {usesType}
                                                        </button>
                                                    )}
                                                    {usesType == "ACH" && (
                                                        <>
                                                            {exclusiveBatch ? (
                                                                <button disabled={loading} onClick={() => setOpenModal(true)}>
                                                                    <UploadOutlined style={{ paddingRight: "10px" }} />
                                                                    Export "{exclusiveBatch?.name}" NACHA file
                                                                </button>
                                                            ) : (
                                                                <button disabled={loading} onClick={() => setOpenModal(true)}>
                                                                    <UploadOutlined style={{ paddingRight: "10px" }} />
                                                                    Export {usesType} batch{checkedBatches.length > 1 && "es"}{checkedBatches.some((batch) => !batch.ach_export_type) && " as..."}
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                    {usesType == "OP" && (
                                                        <>
                                                            <button disabled={loading} onClick={() => exportOP()}>
                                                                <UploadOutlined style={{ paddingRight: "10px" }} />
                                                                Export {usesType} batch{checkedBatches.length > 1 && "es"}
                                                            </button>
                                                            <button disabled={loading} onClick={() => setOpenModal(true)}>
                                                                <UploadOutlined style={{ paddingRight: "10px" }} />
                                                                Pay {usesType} batch{checkedBatches.length > 1 && "es"}
                                                            </button>
                                                        </>
                                                    )}
                                                    {usesType == "NP" && (
                                                        <button disabled={loading} onClick={() => setOpenModal(true)}>
                                                            <UploadOutlined style={{ paddingRight: "10px" }} />
                                                            Finalize {usesType} batch{checkedBatches.length > 1 && "es"}
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                            {/* {checkedBatches[0]?.bills[0].is_exclusive && (
                                                <button onClick={()=>{
                                                    const ids = checkedBatches.map((batch, i) => {
                                                        return batch.id
                                                    })
                                                    dispatch(createNachaFile(ids))
                                                }}>MAKE FILE</button>
                                            )} */}
                                        </>
                                    )}
                                    {isTodaysBatches && !usesType && checkedBatches.length == 0 && total > 0 && (
                                        <button disabled={loading} onClick={() => handleExportBatches()}>
                                            <UploadOutlined style={{ paddingRight: "10px" }} />
                                            Export paid batches
                                        </button>
                                    )}
                                    {checkedBatches.length == 0 && usesType == "ACH" && (
                                        <button disabled={loading} onClick={() => dispatch(achSummaryCsv())}>
                                            <UploadOutlined style={{ paddingRight: "10px" }} />
                                            Export ACH Summary
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <Skeleton active loading={loading}>
                            <Table dataSource={globalBatches} pagination={{ ...pagination, showSizeChanger: true, total: total }} onChange={handleTableChange} rowKey={(r) => r.id}>
                                <Table.Column
                                    key="in"
                                    dataIndex="in"
                                    title={
                                        <Checkbox
                                            checked={checkForAllBatchesSelected()}
                                            onChange={(e) => {
                                                const updateArr = []
                                                const exclusiveBatchCheck = checkForExclusiveBatch()
                                                if (exclusiveBatchCheck) {
                                                    for (let i = 0; i < globalBatches.length; i++) {
                                                        const currBatch = globalBatches[i]
                                                        // console.log(currBatch.id)
                                                        const check = e.target.checked ? !checkBatchId(currBatch.id) : checkBatchId(currBatch.id)
                                                        if (check && !currBatch.is_paid) {
                                                            updateArr.push(currBatch)
                                                        }
                                                    }
                                                    updateChecked(e, globalBatches[0], updateArr)
                                                }
                                            }}
                                        />
                                    }
                                    render={(text, record, index) => {
                                        if (!record.is_paid) {
                                            return <Checkbox disabled={record.is_paid} defaultChecked={false} checked={checkBatchId(record.id)} onChange={(e) => updateChecked(e, record)} />
                                        }
                                        // else return <Checkbox disabled={true} defaultChecked={true}/>
                                    }}
                                />
                                {!usesType && (
                                    <Table.Column
                                        title="Paid"
                                        key="is_paid"
                                        dataIndex="is_paid"
                                        render={(text, record, index) => {
                                            if (text) {
                                                return <CheckOutlined />
                                            } else {
                                                return <CloseOutlined />
                                            }
                                        }}
                                    />
                                )}
                                <Table.Column
                                    title={"Closed"}
                                    key="closed"
                                    dataIndex="closed"
                                    render={(text, record) => <>{record.closed ? "Closed" : ""}</>}
                                    // filterDropdown={({ visible }) => (
                                    //     <ColumnSearchFilter
                                    //         handleFetchInitial={handleFetchInitialGlobalBatches}
                                    //         filterAction={filterPaymentBatches}
                                    //         filterColumn={"batch_date"}
                                    //         filterType={"batch_date"}
                                    //         filters={filters}
                                    //         pagination={pagination}
                                    //         setFilters={setFilters}
                                    //         sort={sort}
                                    //         setSort={setSort}
                                    //         visible={visible}
                                    //     />
                                    // )}
                                    // filterIcon={() => renderSearchOutlinedButton("batch_date_cont")}
                                />
                                <Table.Column
                                    title={renderColumnHeaderTitle("payer_name", "Payer Name")}
                                    key="client_id"
                                    dataIndex="payer_name"
                                    // filterDropdown={({ visible }) => (
                                    //     <ColumnSearchFilter
                                    //         handleFetchInitial={handleFetchInitialGlobalBatches}
                                    //         filterAction={fetchGlobalBatchesFiltered}
                                    //         filterColumn={"client_id"}
                                    //         filterType={"client_id"}
                                    //         filters={filters}
                                    //         pagination={pagination}
                                    //         setFilters={setFilters}
                                    //         sort={sort}
                                    //         setSort={setSort}
                                    //         visible={visible}
                                    //     />
                                    // )}
                                    // filterIcon={() => renderSearchOutlinedButton("client_id")}
                                />
                                <Table.Column title="Vendors" key="vendor_names" dataIndex="vendor_names" render={(text, record, index) => (record.bills[0].is_exclusive ? `*${text}` : text)} />
                                <Table.Column
                                    title="Pay Type"
                                    key="bills_payment_method"
                                    dataIndex="bills_payment_method"
                                    render={(text, record, index) => {
                                        if (record.temp_pay_method) return `${record.temp_pay_method} (${text})`
                                        else return text
                                    }}
                                />
                                <Table.Column
                                    title={"Batch Date"}
                                    key="batch_date"
                                    dataIndex="batch_date"
                                    // filterDropdown={({ visible }) => (
                                    //     <ColumnDateFilter
                                    //         handleFetchInitial={handleFetchInitialGlobalBatches}
                                    //         filterAction={filterPaymentBatches}
                                    //         filterColumn={"batch_date"}
                                    //         filterType={"batch_date"}
                                    //         filters={filters}
                                    //         pagination={pagination}
                                    //         setFilters={setFilters}
                                    //         sort={sort}
                                    //         setSort={setSort}
                                    //         visible={visible}
                                    //     />
                                    // )}
                                    // filterIcon={() => renderSearchOutlinedButton("batch_date_lteq" || "batch_date_gteq")}
                                    render={(text, record, index) => moment(text).format("L")}
                                />
                                {/* TODO: Make money formatting a library function */}
                                <Table.Column
                                    title={"Bills Due Date"}
                                    key="bills_due_date"
                                    dataIndex="bills_due_date"
                                    // filterDropdown={({ visible }) => (
                                    //     <ColumnDateFilter
                                    //         handleFetchInitial={handleFetchInitialGlobalBatches}
                                    //         filterAction={filterPaymentBatches}
                                    //         filterColumn={"bills_due_date"}
                                    //         filterType={"bills_due_date"}
                                    //         filters={filters}
                                    //         pagination={pagination}
                                    //         setFilters={setFilters}
                                    //         sort={sort}
                                    //         setSort={setSort}
                                    //         visible={visible}
                                    //     />
                                    // )}
                                    // filterIcon={() => renderSearchOutlinedButton("bills_due_date_lteq" || "bills_due_date_gteq")}
                                    render={(text, record, index) => moment(text).format("L")}
                                />
                                <Table.Column
                                    title={renderColumnHeaderTitle("batch_total")}
                                    key="batch_total"
                                    dataIndex="batch_total"
                                    align="right"
                                    render={(text, record, index) => formatCurrency(text)}
                                />
                                <Table.Column title="Notes" key="notes" dataIndex="notes" />
                                <Table.Column
                                    title=""
                                    key="action"
                                    render={(text, record) => (
                                        <>
                                            <Dropdown
                                                overlay={
                                                    <Menu>
                                                        {/* <Role action="read" model="payment_batch">
                                                            <Menu.Item key="6" icon={<UploadOutlined />} onClick={() => handleModal(record, true)}>
                                                                <Link to={`/global_batch/todays_batches`}>Export As...</Link>
                                                            </Menu.Item>
                                                        </Role> */}
                                                        {/* <Role action="read" model="payment_batch">
                                                            <Menu.Item key="1" icon={<FolderOutlined />}>
                                                                <Link to={`/clients/${record.client_id}/payment_batches/${record.id}/vendor_payments`}>Vendor Payments</Link>
                                                            </Menu.Item>
                                                        </Role> */}

                                                        <Role action="update" model="payment_batch">
                                                            <Menu.Item key="2" icon={<FolderOutlined />}>
                                                                <Link to={`/global_batch/view/${record.global_batch_id}/bills`}>Bills</Link>
                                                            </Menu.Item>
                                                        </Role>

                                                        {/* <Role action="read" model="payment_batch">
                                                            <Menu.Item key="3" icon={<FileTextOutlined />} onClick={() => voucherCsv(record.id)}>
                                                                Voucher CSV
                                                            </Menu.Item>
                                                        </Role> */}

                                                        <Role action="update" model="payment_batch">
                                                            <Menu.Item key="4" icon={<EditOutlined />}>
                                                                <Link to={`/global_batch/${record.global_batch_id}/edit${usesType ? `?redirect=${usesType}` : ""}`}>Edit Batch</Link>
                                                            </Menu.Item>
                                                        </Role>

                                                        {/* <Role action="destroy" model="payment_batch">
                                                            <Menu.Item
                                                                key="5"
                                                                icon={<DeleteOutlined />}
                                                                onClick={() => handleDeleteClick(record.id)}
                                                                disabled={record.has_been_closed || record.has_bills}>
                                                                Delete Global Batch
                                                            </Menu.Item>
                                                        </Role> */}
                                                    </Menu>
                                                }>
                                                <Button>
                                                    Actions <DownOutlined />
                                                </Button>
                                            </Dropdown>
                                        </>
                                    )}
                                />
                            </Table>
                        </Skeleton>
                    </div>
                </div>
                {/* <div className="card-footer">
                <div className="card-footer-left"></div>
                <div className="card-footer-right pagination"></div>
            </div> */}
            </div>
        </>
    )
}
